import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, Mail, Clock, MapPin, Send, CheckCircle } from 'lucide-react';
import Logo from '@/components/Logo';

// ─── Shared primitives ────────────────────────────────────────────────────────
function SectionLabel({ text }: { text: string }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 7, marginBottom: 16 }}>
      <div style={{ width: 5, height: 5, borderRadius: '50%', background: '#14B8A6' }} />
      <span style={{
        fontFamily: "'JetBrains Mono', monospace",
        fontSize: 9, letterSpacing: '.18em',
        color: 'rgba(20,184,166,.7)',
      }}>{text}</span>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div style={{ marginBottom: 16 }}>
      <label style={{
        display: 'block',
        fontFamily: "'JetBrains Mono', monospace",
        fontSize: 9, letterSpacing: '.12em',
        color: 'rgba(255,255,255,.35)',
        marginBottom: 7,
      }}>{label}</label>
      {children}
    </div>
  );
}

const inputStyle = {
  width: '100%',
  background: 'rgba(255,255,255,.05)',
  border: '1px solid rgba(255,255,255,.09)',
  borderRadius: 9,
  padding: '10px 14px',
  fontSize: 12,
  color: '#fff',
  fontFamily: "'Outfit', sans-serif",
  fontWeight: 300,
  outline: 'none',
};

export default function Contact() {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({ name: '', email: '', subject: '', message: '' });
  const [status, setStatus] = useState<'idle' | 'sending' | 'success' | 'error'>('idle');
  const [errorMsg, setErrorMsg] = useState('');
  const [focusedField, setFocusedField] = useState<string | null>(null);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
    setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setStatus('sending');
    setErrorMsg('');
    try {
      const fd = new FormData();
      fd.append('_subject', `Contact form: ${formData.subject}`);
      fd.append('_template', 'box');
      fd.append('_captcha', 'false');
      fd.append('_replyto', formData.email);
      fd.append('Name', formData.name);
      fd.append('Email', formData.email);
      fd.append('Subject', formData.subject);
      fd.append('Message', formData.message);

      const response = await fetch('https://formsubmit.co/ajax/info@unischedule.me', {
        method: 'POST',
        body: fd,
        headers: { Accept: 'application/json' },
      });
      // FormSubmit returns HTTP 200 even on failure — the body is the source of truth.
      const result = await response.json().catch(() => ({ success: 'false' }));
      if (result.success !== 'true') throw new Error(result.message || 'Failed to send');

      setStatus('success');
      setFormData({ name: '', email: '', subject: '', message: '' });
      setTimeout(() => setStatus('idle'), 6000);
    } catch {
      setStatus('error');
      setErrorMsg('Failed to send. Please email info@unischedule.me directly.');
    }
  };

  const inputFocusStyle = (name: string) => ({
    ...inputStyle,
    borderColor: focusedField === name ? 'rgba(20,184,166,.5)' : 'rgba(255,255,255,.09)',
    transition: 'border-color .2s',
  });

  return (
    <div style={{ background: '#0A0A0A', fontFamily: "'Outfit', sans-serif", color: '#fff', minHeight: '100vh' }}>

      {/* ── Nav ─────────────────────────────────────────── */}
      <header style={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        padding: '18px clamp(24px,5vw,56px)',
        borderBottom: '1px solid rgba(255,255,255,.07)',
        background: '#0A0A0A', position: 'sticky', top: 0, zIndex: 50,
      }}>
        <Logo size="md" clickable navigateTo="/" scheduleWhite={true} />
        <NavBackBtn onClick={() => navigate('/')} />
      </header>

      {/* ── Hero ────────────────────────────────────────── */}
      <section style={{
        padding: 'clamp(48px,6vw,72px) clamp(24px,5vw,56px) clamp(40px,5vw,56px)',
        borderBottom: '1px solid rgba(255,255,255,.07)',
        position: 'relative', overflow: 'hidden',
      }}>
        <div style={{
          position: 'absolute', top: -60, right: -60, width: 360, height: 360,
          background: 'radial-gradient(circle,rgba(20,184,166,.1) 0%,transparent 65%)',
          pointerEvents: 'none',
        }} />
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7 }}
          style={{ position: 'relative', zIndex: 1, maxWidth: 600 }}
        >
          <SectionLabel text="CONTACT" />
          <h1 style={{
            fontFamily: "'Instrument Serif', serif",
            fontSize: 'clamp(42px,6vw,68px)', fontWeight: 400,
            letterSpacing: '-.03em', lineHeight: .95, marginBottom: 20,
          }}>
            Let's <em style={{ fontStyle: 'italic', color: 'rgba(255,255,255,.3)' }}>talk</em>
            <br />about it
          </h1>
          <p style={{ fontSize: 13, lineHeight: 1.75, color: 'rgba(255,255,255,.4)', fontWeight: 300, maxWidth: 420 }}>
            Have a question, found a bug, or want to collaborate? Drop us a message — we read every single one.
          </p>
        </motion.div>
      </section>

      {/* ── Body ────────────────────────────────────────── */}
      <main
        className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.5fr)] gap-8 lg:gap-8"
        style={{
          padding: 'clamp(40px,5vw,56px) clamp(24px,5vw,56px)',
          borderBottom: '1px solid rgba(255,255,255,.07)',
          alignItems: 'start',
        }}
      >

        {/* Left: info + topics */}
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.6, delay: 0.1 }}
          style={{ display: 'flex', flexDirection: 'column', gap: 12 }}
        >
          {/* Contact info card */}
          <div style={{
            background: 'rgba(255,255,255,.03)',
            border: '1px solid rgba(255,255,255,.07)',
            borderRadius: 14, padding: '22px 24px',
          }}>
            {[
              { Icon: Mail,   label: 'EMAIL',         value: <a href="mailto:info@unischedule.me" style={{ color: 'rgba(255,255,255,.6)', textDecoration: 'none' }}>info@unischedule.me</a> },
              { Icon: Clock,  label: 'RESPONSE TIME', value: 'Within 24–48 hours' },
              { Icon: MapPin, label: 'BASED IN',       value: 'Peshawar, Pakistan' },
            ].map(({ Icon, label, value }, i, arr) => (
              <div key={label} style={{
                display: 'flex', alignItems: 'flex-start', gap: 12,
                padding: '12px 0',
                borderBottom: i < arr.length - 1 ? '1px solid rgba(255,255,255,.06)' : 'none',
              }}>
                <div style={{
                  width: 32, height: 32, borderRadius: 9, flexShrink: 0,
                  background: 'rgba(20,184,166,.1)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  color: '#14B8A6',
                }}>
                  <Icon size={15} aria-hidden />
                </div>
                <div>
                  <div style={{ fontFamily: "'JetBrains Mono',monospace", fontSize: 9, letterSpacing: '.12em', color: 'rgba(255,255,255,.3)', marginBottom: 3 }}>{label}</div>
                  <div style={{ fontSize: 12, color: 'rgba(255,255,255,.6)', fontWeight: 300 }}>{value}</div>
                </div>
              </div>
            ))}
          </div>

          {/* Topics card */}
          <div style={{
            background: 'rgba(255,255,255,.03)',
            border: '1px solid rgba(255,255,255,.07)',
            borderRadius: 14, padding: '22px 24px',
          }}>
            <div style={{ fontFamily: "'JetBrains Mono',monospace", fontSize: 9, letterSpacing: '.14em', color: 'rgba(255,255,255,.3)', marginBottom: 14 }}>
              WHAT WE CAN HELP WITH
            </div>
            {['Technical support', 'Feature requests', 'Bug reports', 'General inquiries', 'Partnership opportunities'].map((t, i, arr) => (
              <div key={t} style={{
                display: 'flex', alignItems: 'center', gap: 8,
                padding: '8px 0', fontSize: 12,
                color: 'rgba(255,255,255,.45)', fontWeight: 300,
                borderBottom: i < arr.length - 1 ? '1px solid rgba(255,255,255,.05)' : 'none',
              }}>
                <span style={{ fontFamily: "'JetBrains Mono',monospace", fontSize: 9, color: 'rgba(20,184,166,.5)', minWidth: 20 }}>
                  {String(i + 1).padStart(2, '0')}
                </span>
                {t}
              </div>
            ))}
          </div>
        </motion.div>

        {/* Right: form */}
        <motion.div
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.6, delay: 0.15 }}
          style={{
            background: 'rgba(255,255,255,.03)',
            border: '1px solid rgba(255,255,255,.07)',
            borderRadius: 14, padding: '28px 30px',
          }}
        >
          <div style={{ marginBottom: 24 }}>
            <div style={{ fontFamily: "'Instrument Serif',serif", fontSize: 26, fontWeight: 400, letterSpacing: '-.02em', marginBottom: 4 }}>
              Send a message
            </div>
            <div style={{ fontSize: 11, color: 'rgba(255,255,255,.3)', fontWeight: 300 }}>All fields are required</div>
          </div>

          {/* Success banner */}
          {status === 'success' && (
            <motion.div
              initial={{ opacity: 0, y: -8 }}
              animate={{ opacity: 1, y: 0 }}
              style={{
                background: 'rgba(20,184,166,.08)',
                border: '1px solid rgba(20,184,166,.2)',
                borderRadius: 10, padding: '14px 16px',
                display: 'flex', alignItems: 'flex-start', gap: 10,
                marginBottom: 18,
              }}
            >
              <CheckCircle size={16} color="#14B8A6" style={{ flexShrink: 0, marginTop: 1 }} aria-hidden />
              <div>
                <div style={{ fontSize: 12, fontWeight: 500, color: '#14B8A6', marginBottom: 2 }}>Message sent!</div>
                <div style={{ fontSize: 11, color: 'rgba(255,255,255,.4)', fontWeight: 300 }}>We'll get back to you within 24–48 hours.</div>
              </div>
            </motion.div>
          )}

          {/* Error banner */}
          {status === 'error' && (
            <div style={{
              background: 'rgba(239,68,68,.08)',
              border: '1px solid rgba(239,68,68,.2)',
              borderRadius: 10, padding: '14px 16px',
              fontSize: 12, color: 'rgba(255,120,120,.8)',
              marginBottom: 18,
            }}>{errorMsg}</div>
          )}

          <form onSubmit={handleSubmit}>
            {/* Name + Email row */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Field label="NAME">
                <input
                  name="name" type="text" required
                  value={formData.name} onChange={handleChange}
                  placeholder="Your name"
                  disabled={status === 'sending'}
                  style={inputFocusStyle('name')}
                  onFocus={() => setFocusedField('name')}
                  onBlur={() => setFocusedField(null)}
                />
              </Field>
              <Field label="EMAIL">
                <input
                  name="email" type="email" required
                  value={formData.email} onChange={handleChange}
                  placeholder="you@example.com"
                  disabled={status === 'sending'}
                  style={inputFocusStyle('email')}
                  onFocus={() => setFocusedField('email')}
                  onBlur={() => setFocusedField(null)}
                />
              </Field>
            </div>

            <Field label="SUBJECT">
              <input
                name="subject" type="text" required
                value={formData.subject} onChange={handleChange}
                placeholder="What's this about?"
                disabled={status === 'sending'}
                style={inputFocusStyle('subject')}
                onFocus={() => setFocusedField('subject')}
                onBlur={() => setFocusedField(null)}
              />
            </Field>

            <Field label="MESSAGE">
              <textarea
                name="message" required rows={5}
                value={formData.message} onChange={handleChange}
                placeholder="Tell us more…"
                disabled={status === 'sending'}
                style={{ ...inputFocusStyle('message'), resize: 'none' }}
                onFocus={() => setFocusedField('message')}
                onBlur={() => setFocusedField(null)}
              />
            </Field>

            <button
              type="submit"
              disabled={status === 'sending' || status === 'success'}
              style={{
                width: '100%',
                background: status === 'success' ? 'rgba(20,184,166,.2)' : 'transparent',
                color: status === 'success' ? '#14B8A6' : '#14B8A6',
                border: `1px solid ${status === 'success' ? 'rgba(20,184,166,.5)' : 'rgba(20,184,166,.5)'}`,
                borderRadius: 0,
                padding: '12px', fontSize: 13, fontWeight: 500,
                fontFamily: "'Outfit', sans-serif",
                cursor: status === 'sending' ? 'not-allowed' : 'pointer',
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                opacity: status === 'sending' ? .6 : 1,
                transition: 'background .2s, opacity .2s',
                marginTop: 4,
              }}
              onMouseEnter={(e) => {
                if (status !== 'sending' && status !== 'success') {
                  e.currentTarget.style.background = 'rgba(20,184,166,.1)';
                }
              }}
              onMouseLeave={(e) => {
                if (status !== 'sending' && status !== 'success') {
                  e.currentTarget.style.background = 'transparent';
                }
              }}
            >
              {status === 'sending' ? (
                <>
                  <div style={{
                    width: 14, height: 14, borderRadius: '50%',
                    border: '2px solid rgba(0,0,0,.4)',
                    borderTopColor: '#0A0A0A',
                    animation: 'spin .7s linear infinite',
                  }} />
                  Sending…
                </>
              ) : status === 'success' ? (
                <><CheckCircle size={14} aria-hidden /> Sent</>
              ) : (
                <><Send size={14} aria-hidden /> Send Message</>
              )}
            </button>

            <p style={{ textAlign: 'center', fontSize: 10, color: 'rgba(255,255,255,.2)', marginTop: 10 }}>
              By submitting, you agree to our{' '}
              <button
                type="button"
                onClick={() => navigate('/privacy-policy')}
                style={{ color: 'rgba(20,184,166,.6)', background: 'none', border: 'none', cursor: 'pointer', fontSize: 10, fontFamily: "'Outfit',sans-serif" }}
              >
                Privacy Policy
              </button>
            </p>
          </form>
        </motion.div>
      </main>

      {/* ── Quick links ──────────────────────────────────── */}
      <div
        className="flex flex-col sm:flex-row items-center justify-between gap-4"
        style={{
          padding: '28px clamp(24px,5vw,56px)',
          borderBottom: '1px solid rgba(255,255,255,.07)',
        }}
      >
        <span style={{ fontFamily: "'JetBrains Mono',monospace", fontSize: 9, letterSpacing: '.14em', color: 'rgba(255,255,255,.3)' }}>
          LOOKING FOR SOMETHING ELSE?
        </span>
        <div className="flex flex-wrap justify-center gap-2">
          {[
            { label: 'Get Started',      path: '/student-form'    },
            { label: 'Privacy Policy',   path: '/privacy-policy'  },
            { label: 'Terms of Service', path: '/terms-of-service'},
          ].map(({ label, path }) => (
            <QuickLink key={label} onClick={() => navigate(path)}>{label}</QuickLink>
          ))}
        </div>
      </div>

      {/* ── Footer ──────────────────────────────────────── */}
      <footer
        className="flex flex-col sm:flex-row justify-between items-center gap-4"
        style={{
          padding: '18px clamp(24px,5vw,56px)',
        }}
      >
        <span style={{ fontFamily: "'JetBrains Mono',monospace", fontSize: 9, letterSpacing: '.1em', color: 'rgba(255,255,255,.2)' }}>
          © 2025 UNISCHEDULE
        </span>
        <span className="text-center" style={{ fontFamily: "'JetBrains Mono',monospace", fontSize: 9, letterSpacing: '.1em', color: 'rgba(255,255,255,.2)' }}>
          BUILT WITH ♥ BY{' '}
          <a
            href="https://hakimi.tech"
            target="_blank"
            rel="noopener noreferrer"
            title="hakimi.tech"
            style={{ color: '#14B8A6', textDecoration: 'underline', textDecorationColor: 'rgba(20,184,166,.4)', textUnderlineOffset: 3, cursor: 'pointer', fontFamily: "'JetBrains Mono',monospace", fontSize: 9, letterSpacing: '.1em', transition: 'color .2s, text-decoration-color .2s' }}
            onMouseEnter={e => { e.currentTarget.style.color = '#2DD4BF'; e.currentTarget.style.textDecorationColor = '#2DD4BF'; }}
            onMouseLeave={e => { e.currentTarget.style.color = '#14B8A6'; e.currentTarget.style.textDecorationColor = 'rgba(20,184,166,.4)'; }}
          >FAISAL HAKIMI</a>
        </span>
      </footer>

      <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
    </div>
  );
}

// ─── Sub-components ───────────────────────────────────────────────────────────

function NavBackBtn({ onClick }: { onClick: () => void }) {
  const [hov, setHov] = useState(false);
  return (
    <button
      onClick={onClick}
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      style={{
        display: 'flex', alignItems: 'center', gap: 6,
        fontSize: 12, fontFamily: "'Outfit',sans-serif",
        color: hov ? '#fff' : '#14B8A6',
        background: hov ? 'rgba(20,184,166,.1)' : 'transparent',
        border: '1px solid rgba(20,184,166,.5)',
        borderRadius: 0, padding: '7px 14px', cursor: 'pointer',
        transition: 'color .2s, background .2s',
      }}
    >
      <ArrowLeft size={13} aria-hidden /> Back to Home
    </button>
  );
}

function QuickLink({ children, onClick }: { children: React.ReactNode; onClick: () => void }) {
  const [hov, setHov] = useState(false);
  return (
    <button
      onClick={onClick}
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      style={{
        fontSize: 11, fontFamily: "'Outfit',sans-serif",
        color: hov ? '#fff' : '#14B8A6',
        background: hov ? 'rgba(20,184,166,.1)' : 'transparent',
        border: '1px solid rgba(20,184,166,.5)',
        borderRadius: 0, padding: '7px 14px', cursor: 'pointer',
        transition: 'color .2s, background .2s',
      }}
    >
      {children}
    </button>
  );
}